import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getItemDetails,
  updateItem,
  assignItem,
  removeItemInstance,
  updateItemInstance,
  syncItemInstance,
  syncItem,
  reprocessItem,
  cancelReprocess,
  deleteItem,
  getTaskStatus,
} from '../api/items';

// Polling constants
const POLLING_START_DELAY_MS = 10000; // Start polling after 10s if no WebSocket
const POLLING_INTERVAL_MS = 3000; // Poll every 3s
const MAX_TIMEOUT_MS = 90000; // Give up after 90s

/**
 * Custom hook for fetching and managing item details with all instances
 *
 * @param {string} itemHash - The item hash to fetch details for
 * @param {string} [instanceId] - Optional instance ID for issue-specific context
 * @param {Object} [lastMessage] - Optional WebSocket last message for real-time updates
 * @returns {Object} Item details state, mutations, and handlers
 */
export function useItemDetail(itemHash, instanceId = null, lastMessage = null) {
  const queryClient = useQueryClient();

  // State for tracking pending AI reprocess tasks (local fallback, cleared when persisted state available)
  const [localPendingTaskId, setLocalPendingTaskId] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [reprocessError, setReprocessError] = useState(null);
  const pollingIntervalRef = useRef(null);
  const timeoutsRef = useRef({ poll: null, max: null });

  // State for elapsed time calculation
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Query for item details (canonical item + instances with issue info)
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['item', 'details', itemHash],
    queryFn: () => getItemDetails(itemHash),
    enabled: !!itemHash,
    staleTime: 30000, // Consider fresh for 30 seconds
    gcTime: 300000, // Keep in cache for 5 minutes
  });

  // Fields that sync from canonical item to instances
  const SYNC_FIELDS = ['url', 'authors', 'published', 'timeToRead', 'summary', 'type', 'domain', 'qualityScore', 'relevanceScore'];
  const CONDITIONAL_SYNC_FIELDS = ['title', 'category'];

  // Mutation for updating canonical item (with optimistic updates)
  const updateItemMutation = useMutation({
    mutationFn: (updates) => updateItem(itemHash, updates),
    onMutate: async (updates) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['item', 'details', itemHash] });
      await queryClient.cancelQueries({ queryKey: ['instances'], exact: false });

      // Snapshot previous values
      const previousItemData = queryClient.getQueryData(['item', 'details', itemHash]);

      // Build synced fields for instances
      const syncedFields = {};
      SYNC_FIELDS.forEach((field) => {
        if (updates[field] !== undefined) {
          syncedFields[field] = updates[field];
        }
      });
      // Handle nested fields
      if (updates.meta?.domain !== undefined) {
        syncedFields.domain = updates.meta.domain;
      }
      if (updates.ai?.qualityScore !== undefined) {
        syncedFields.qualityScore = updates.ai.qualityScore;
      }

      // Optimistically update the item details cache
      queryClient.setQueryData(['item', 'details', itemHash], (old) => {
        if (!old) return old;
        return {
          ...old,
          item: { ...old.item, ...updates },
          // Also update instances with synced fields
          instances: old.instances.map((inst) => {
            const instUpdates = { ...syncedFields };
            // Conditional sync: only if instance field is blank
            CONDITIONAL_SYNC_FIELDS.forEach((field) => {
              if (updates[field] !== undefined && !inst[field]) {
                instUpdates[field] = updates[field];
              }
            });
            return Object.keys(instUpdates).length > 0 ? { ...inst, ...instUpdates } : inst;
          }),
        };
      });

      // Optimistically update all IssuesPage instance caches
      queryClient.setQueriesData({ queryKey: ['instances'], exact: false }, (old) => {
        if (!old?.instances) return old;
        return {
          ...old,
          instances: old.instances.map((inst) => {
            if (inst.hash !== itemHash) return inst;
            const instUpdates = { ...syncedFields };
            CONDITIONAL_SYNC_FIELDS.forEach((field) => {
              if (updates[field] !== undefined && !inst[field]) {
                instUpdates[field] = updates[field];
              }
            });
            return Object.keys(instUpdates).length > 0 ? { ...inst, ...instUpdates } : inst;
          }),
        };
      });

      return { previousItemData };
    },
    onError: (err, updates, context) => {
      // Roll back to the previous value on error
      if (context?.previousItemData) {
        queryClient.setQueryData(['item', 'details', itemHash], context.previousItemData);
      }
      // Refetch instance queries to restore correct state
      queryClient.invalidateQueries({ queryKey: ['instances'], exact: false });
    },
    onSuccess: () => {
      // Only invalidate items list for search results (different data shape)
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  // Mutation for assigning item to an issue
  const assignItemMutation = useMutation({
    mutationFn: ({ issueNumber, category }) =>
      assignItem(itemHash, { issueNumber, category }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', 'details', itemHash] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  // Mutation for removing an instance (unassigning from issue)
  const removeInstanceMutation = useMutation({
    mutationFn: (instId) => removeItemInstance(instId),
    onMutate: async (instId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['item', 'details', itemHash] });
      await queryClient.cancelQueries({ queryKey: ['instances'], exact: false });

      // Snapshot previous values for rollback
      const previousItemData = queryClient.getQueryData(['item', 'details', itemHash]);

      // Optimistically remove the instance from item details cache
      queryClient.setQueryData(['item', 'details', itemHash], (old) => {
        if (!old) return old;
        return {
          ...old,
          instances: old.instances.filter((inst) => inst.id !== instId),
        };
      });

      // Optimistically remove the instance from all IssuesPage instance caches
      queryClient.setQueriesData({ queryKey: ['instances'], exact: false }, (old) => {
        if (!old?.instances) return old;
        return {
          ...old,
          instances: old.instances.filter((inst) => inst.id !== instId),
        };
      });

      return { previousItemData };
    },
    onError: (err, instId, context) => {
      // Roll back to the previous value on error
      if (context?.previousItemData) {
        queryClient.setQueryData(['item', 'details', itemHash], context.previousItemData);
      }
      // Refetch instance queries to restore correct state
      queryClient.invalidateQueries({ queryKey: ['instances'], exact: false });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', 'details', itemHash] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['instances'], exact: false });
    },
  });

  // Mutation for updating an instance (with optimistic updates, no refetch)
  const updateInstanceMutation = useMutation({
    mutationFn: ({ instanceId: instId, updates }) => updateItemInstance(instId, updates),
    onMutate: async ({ instanceId: instId, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['item', 'details', itemHash] });
      await queryClient.cancelQueries({ queryKey: ['instances'], exact: false });

      // Snapshot previous values for rollback
      const previousItemData = queryClient.getQueryData(['item', 'details', itemHash]);

      // Optimistically update the instance in the item details cache
      queryClient.setQueryData(['item', 'details', itemHash], (old) => {
        if (!old) return old;
        return {
          ...old,
          instances: old.instances.map((inst) =>
            inst.id === instId ? { ...inst, ...updates } : inst
          ),
        };
      });

      // Optimistically update all IssuesPage instance caches
      queryClient.setQueriesData({ queryKey: ['instances'], exact: false }, (old) => {
        if (!old?.instances) return old;
        return {
          ...old,
          instances: old.instances.map((inst) =>
            inst.id === instId ? { ...inst, ...updates } : inst
          ),
        };
      });

      return { previousItemData };
    },
    onError: (err, variables, context) => {
      // Roll back to the previous value on error
      if (context?.previousItemData) {
        queryClient.setQueryData(['item', 'details', itemHash], context.previousItemData);
      }
      // Refetch instance queries to restore correct state
      queryClient.invalidateQueries({ queryKey: ['instances'], exact: false });
    },
    // No onSuccess invalidation - optimistic updates are the source of truth
  });

  // Mutation for syncing instance from canonical item
  const syncInstanceMutation = useMutation({
    mutationFn: (instId) => syncItemInstance(instId),
    onSuccess: () => {
      // Refetch to get updated instance data
      queryClient.invalidateQueries({ queryKey: ['item', 'details', itemHash] });
      queryClient.invalidateQueries({ queryKey: ['instances'], exact: false });
    },
  });

  // Mutation for syncing item to Algolia
  const syncItemMutation = useMutation({
    mutationFn: () => syncItem(itemHash),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', 'details', itemHash] });
    },
  });

  // Mutation for reprocessing item (AI analysis) - now returns 202 with taskId
  const reprocessItemMutation = useMutation({
    mutationFn: () => reprocessItem(itemHash),
    onMutate: async () => {
      // Optimistically update status to 'process' immediately for UI feedback
      await queryClient.cancelQueries({ queryKey: ['item', 'details', itemHash] });

      const previousData = queryClient.getQueryData(['item', 'details', itemHash]);

      queryClient.setQueryData(['item', 'details', itemHash], (old) => {
        if (!old) return old;
        return {
          ...old,
          item: {
            ...old.item,
            status: 'process',
          },
        };
      });

      // Also update items list cache for immediate feedback
      queryClient.setQueriesData({ queryKey: ['items'] }, (oldData) => {
        if (!oldData?.items) return oldData;
        return {
          ...oldData,
          items: oldData.items.map((item) =>
            item.hash === itemHash ? { ...item, status: 'process' } : item
          ),
        };
      });

      return { previousData };
    },
    onSuccess: (response) => {
      // Store the taskId locally for tracking via WebSocket
      // This is a fallback until the item data is updated with processingTaskId
      if (response.taskId) {
        setLocalPendingTaskId(response.taskId);
        setReprocessError(null);

        // Update cache with processing task info so it persists across navigation
        const processingStartedAt = new Date().toISOString();
        queryClient.setQueryData(['item', 'details', itemHash], (old) => {
          if (!old) return old;
          return {
            ...old,
            item: {
              ...old.item,
              status: 'process', // Ensure status is set
              ai: {
                ...old.item.ai,
                processingTaskId: response.taskId,
                processingStartedAt,
              },
            },
          };
        });
      }
      // Don't invalidate yet - wait for WebSocket notification or polling
    },
    onError: (error, variables, context) => {
      // Roll back status on error
      if (context?.previousData) {
        queryClient.setQueryData(['item', 'details', itemHash], context.previousData);
      }
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setLocalPendingTaskId(null);
      setReprocessError(error.message || 'Failed to start AI reprocessing');
    },
  });

  // Mutation for canceling reprocess (clearing processing state)
  const cancelReprocessMutation = useMutation({
    mutationFn: () => cancelReprocess(itemHash),
    onSuccess: () => {
      setLocalPendingTaskId(null);
      setReprocessError(null);
      queryClient.invalidateQueries({ queryKey: ['item', 'details', itemHash] });
    },
  });

  // Mutation for deleting item and all instances
  const deleteItemMutation = useMutation({
    mutationFn: () => deleteItem(itemHash),
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['items'] });
      await queryClient.cancelQueries({ queryKey: ['instances'], exact: false });

      // Optimistically remove the item from all items list caches
      queryClient.setQueriesData({ queryKey: ['items'] }, (oldData) => {
        if (!oldData || !oldData.items) return oldData;
        return {
          ...oldData,
          items: oldData.items.filter((item) => item.hash !== itemHash),
        };
      });

      // Optimistically remove all instances of this item from IssuesPage caches
      queryClient.setQueriesData({ queryKey: ['instances'], exact: false }, (old) => {
        if (!old?.instances) return old;
        return {
          ...old,
          instances: old.instances.filter((inst) => inst.hash !== itemHash),
        };
      });
    },
    onSuccess: () => {
      // Invalidate all item queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['instances'], exact: false });
      queryClient.removeQueries({ queryKey: ['item', 'details', itemHash] });
    },
    onError: () => {
      // On error, refetch to restore the correct state
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['instances'], exact: false });
    },
  });

  // Handle completion of reprocess task - clears state and updates cache
  const handleReprocessComplete = useCallback(
    (aiData) => {
      setLocalPendingTaskId(null);
      setIsPolling(false);
      setReprocessError(null);

      // Clear any pending timeouts/intervals
      if (timeoutsRef.current.poll) clearTimeout(timeoutsRef.current.poll);
      if (timeoutsRef.current.max) clearTimeout(timeoutsRef.current.max);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);

      // Update cache with new AI data (processing state already cleared by backend)
      if (aiData) {
        queryClient.setQueryData(['item', 'details', itemHash], (old) => {
          if (!old) return old;
          return {
            ...old,
            item: { ...old.item, ai: aiData },
          };
        });
      }

      // Always refetch to get correct status and any other backend updates
      // This ensures status is updated after we optimistically set it to 'process'
      queryClient.invalidateQueries({ queryKey: ['item', 'details', itemHash] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
    [itemHash, queryClient]
  );

  // Handle reprocess task error
  const handleReprocessError = useCallback((errorMessage) => {
    setLocalPendingTaskId(null);
    setIsPolling(false);
    setReprocessError(errorMessage || 'AI reprocessing failed');

    // Clear any pending timeouts/intervals
    if (timeoutsRef.current.poll) clearTimeout(timeoutsRef.current.poll);
    if (timeoutsRef.current.max) clearTimeout(timeoutsRef.current.max);
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);

    // Refetch to get cleared processing state and correct status from backend
    queryClient.invalidateQueries({ queryKey: ['item', 'details', itemHash] });
    queryClient.invalidateQueries({ queryKey: ['items'] });
  }, [itemHash, queryClient]);

  // WebSocket message handler for reprocess completion
  useEffect(() => {
    if (!lastMessage) return;

    const { type, data: messageData } = lastMessage;

    // Handle reprocess completion - match by taskId (local or persisted) or by hash
    const persistedTaskId = data?.item?.ai?.processingTaskId;
    const activeTaskId = localPendingTaskId || persistedTaskId;

    if (type === 'item_ai_reprocess_complete') {
      if (messageData?.taskId === activeTaskId || messageData?.hash === itemHash) {
        handleReprocessComplete(messageData.ai);
      }
    }

    if (type === 'item_ai_reprocess_error') {
      if (messageData?.taskId === activeTaskId || messageData?.hash === itemHash) {
        handleReprocessError(messageData.error);
      }
    }

    // Note: We intentionally do NOT listen for 'item_ai_analysis_complete' here.
    // That message is sent after the first AI pass but before blurb generation.
    // The reprocessTask sends 'item_ai_reprocess_complete' after both passes are done.
  }, [lastMessage, localPendingTaskId, data, itemHash, handleReprocessComplete, handleReprocessError]);

  // Polling fallback - starts after delay if WebSocket message not received
  // Note: Only applies to local pending task ID (brief moment after clicking)
  // Persisted state will be picked up from item.ai.processingTaskId
  useEffect(() => {
    if (!localPendingTaskId) return;

    // Max timeout - give up after 90s
    timeoutsRef.current.max = setTimeout(() => {
      console.warn(`Reprocess timeout for taskId: ${localPendingTaskId}`);
      setLocalPendingTaskId(null);
      setIsPolling(false);
      // Refetch item to get latest state
      queryClient.invalidateQueries({ queryKey: ['item', 'details', itemHash] });
    }, MAX_TIMEOUT_MS);

    return () => {
      if (timeoutsRef.current.poll) clearTimeout(timeoutsRef.current.poll);
      if (timeoutsRef.current.max) clearTimeout(timeoutsRef.current.max);
    };
  }, [localPendingTaskId, itemHash, queryClient]);

  // Poll task status when isPolling is true
  // TODO: Re-enable polling when task status endpoint is working
  // useEffect(() => {
  //   if (!isPolling || !pendingTaskId) return;
  //
  //   const pollStatus = async () => {
  //     try {
  //       const status = await getTaskStatus(pendingTaskId);
  //       if (status.state === 'completed') {
  //         handleReprocessComplete(status.result?.ai);
  //       } else if (status.state === 'failed') {
  //         handleReprocessError(status.errorMessage || 'AI analysis failed');
  //       }
  //       // If still running, continue polling
  //     } catch (error) {
  //       console.error('Failed to poll task status:', error);
  //       // Continue polling despite error
  //     }
  //   };
  //
  //   // Poll immediately, then on interval
  //   pollStatus();
  //   pollingIntervalRef.current = setInterval(pollStatus, POLLING_INTERVAL_MS);
  //
  //   return () => {
  //     if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
  //   };
  // }, [isPolling, pendingTaskId, handleReprocessComplete, handleReprocessError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutsRef.current.poll) clearTimeout(timeoutsRef.current.poll);
      if (timeoutsRef.current.max) clearTimeout(timeoutsRef.current.max);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, []);

  // Memoized derived data
  const item = useMemo(() => data?.item || null, [data]);
  const instances = useMemo(() => data?.instances || [], [data]);

  // Derive processing state from item data (persisted) or local state (temporary)
  // Persisted state takes precedence as it survives page reloads
  const persistedTaskId = item?.ai?.processingTaskId;
  const processingStartedAt = item?.ai?.processingStartedAt;
  const pendingTaskId = persistedTaskId || localPendingTaskId;

  // Determine if reprocessing is in progress
  // True if: API call in flight OR we have a pending task (local or persisted)
  const isReprocessingTask = reprocessItemMutation.isPending || !!pendingTaskId;

  // Elapsed time calculation for processing timer
  useEffect(() => {
    if (!processingStartedAt) {
      setElapsedSeconds(0);
      return;
    }

    const startTime = new Date(processingStartedAt).getTime();

    const updateElapsed = () => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [processingStartedAt]);

  // Clear local pending task ID when persisted state is available
  // This prevents showing stale local state when persisted state is cleared
  useEffect(() => {
    if (persistedTaskId && localPendingTaskId) {
      setLocalPendingTaskId(null);
    }
  }, [persistedTaskId, localPendingTaskId]);

  // Check task status on load when item has a persisted processingTaskId
  // This handles edge cases where the task failed but WebSocket message wasn't received
  const hasCheckedTaskRef = useRef(null); // Track which taskId we've checked
  useEffect(() => {
    // Only check if we have a persisted task ID and haven't checked this one yet
    if (!persistedTaskId || hasCheckedTaskRef.current === persistedTaskId) return;

    const checkTaskStatus = async () => {
      try {
        const status = await getTaskStatus(persistedTaskId);
        hasCheckedTaskRef.current = persistedTaskId;

        if (status.state === 'failed') {
          // Task failed - clear processing state and show error
          console.warn(`Task ${persistedTaskId} failed:`, status.errorMessage);
          handleReprocessError(status.errorMessage || 'AI processing failed');

          // Also clear the processing state on the backend
          try {
            await cancelReprocess(itemHash);
          } catch (cancelErr) {
            console.error('Failed to clear processing state:', cancelErr);
          }
        } else if (status.state === 'completed') {
          // Task completed but we missed the WebSocket message - refetch to get results
          console.info(`Task ${persistedTaskId} completed, refreshing data`);
          handleReprocessComplete(status.result?.ai);
        }
        // If still running (created, scheduled, submitted), do nothing - WebSocket will notify
      } catch (err) {
        console.error('Failed to check task status:', err);
        // Don't set error state here - the task might still be running
        // Just mark as checked so we don't spam requests
        hasCheckedTaskRef.current = persistedTaskId;
      }
    };

    checkTaskStatus();
  }, [persistedTaskId, itemHash, handleReprocessError, handleReprocessComplete]);

  // Find current instance when instanceId is provided (for issue-specific context)
  const currentInstance = useMemo(() => {
    if (!instanceId || !instances.length) return null;
    return instances.find((inst) => inst.id === instanceId) || null;
  }, [instanceId, instances]);

  // Separate published and unpublished instances
  const { publishedInstances, unpublishedInstances } = useMemo(() => {
    const published = [];
    const unpublished = [];

    instances.forEach((instance) => {
      if (instance.issue?.status === 'published') {
        published.push(instance);
      } else {
        unpublished.push(instance);
      }
    });

    // Sort by issue number descending (most recent first)
    published.sort((a, b) => b.issueNumber - a.issueNumber);
    unpublished.sort((a, b) => b.issueNumber - a.issueNumber);

    return { publishedInstances: published, unpublishedInstances: unpublished };
  }, [instances]);

  return useMemo(
    () => ({
      // Data
      item,
      instances,
      publishedInstances,
      unpublishedInstances,
      currentInstance,
      instanceId,

      // Query state
      isLoading,
      error,
      refetch,

      // Mutations
      updateItem: updateItemMutation.mutateAsync,
      isUpdatingItem: updateItemMutation.isPending,

      assignToIssue: assignItemMutation.mutateAsync,
      isAssigning: assignItemMutation.isPending,

      removeInstance: removeInstanceMutation.mutateAsync,
      isRemoving: removeInstanceMutation.isPending,

      updateInstance: updateInstanceMutation.mutateAsync,
      isUpdatingInstance: updateInstanceMutation.isPending,

      syncInstanceFromCanonical: syncInstanceMutation.mutateAsync,
      isSyncingInstance: syncInstanceMutation.isPending,

      syncToAlgolia: syncItemMutation.mutateAsync,
      isSyncing: syncItemMutation.isPending,

      reprocessAI: reprocessItemMutation.mutateAsync,
      isReprocessing: isReprocessingTask,
      pendingTaskId,
      processingStartedAt,
      elapsedSeconds,
      isPolling,
      reprocessError,

      cancelReprocess: cancelReprocessMutation.mutateAsync,
      isCancelingReprocess: cancelReprocessMutation.isPending,

      deleteItem: deleteItemMutation.mutateAsync,
      isDeleting: deleteItemMutation.isPending,
    }),
    [
      item,
      instances,
      publishedInstances,
      unpublishedInstances,
      currentInstance,
      instanceId,
      isLoading,
      error,
      refetch,
      updateItemMutation,
      assignItemMutation,
      removeInstanceMutation,
      updateInstanceMutation,
      syncInstanceMutation,
      syncItemMutation,
      reprocessItemMutation,
      isReprocessingTask,
      pendingTaskId,
      processingStartedAt,
      elapsedSeconds,
      isPolling,
      reprocessError,
      cancelReprocessMutation,
      deleteItemMutation,
    ]
  );
}

export default useItemDetail;
